import { z } from 'zod';

/**
 * The two endpoints VC-0 actually ships.
 *
 * Both are deliberately dull. `/health` is consumed by uptime checks and must never
 * reveal anything about the account it runs in; `/v1/platform/info` tells an
 * integrator which API surface they are talking to before they authenticate.
 *
 * The audit rule for both: if a field would help an attacker fingerprint our
 * infrastructure (account id, role ARN, VPC, file paths, dependency versions), it
 * does not belong here.
 */
export const PlatformEnvironments = ['local', 'dev', 'prod'] as const;
export type PlatformEnvironment = (typeof PlatformEnvironments)[number];

export const healthResponseSchema = z
  .object({
    status: z.literal('ok'),
    service: z.literal('verydia-control-api'),
    version: z.string().describe('Deployed application version.'),
    timestamp: z.iso.datetime().describe('Server time, ISO-8601 UTC.'),
    uptime_seconds: z.number().nonnegative().describe('Seconds since this instance started.'),
  })
  .meta({
    id: 'HealthResponse',
    title: 'HealthResponse',
    description: 'The service is able to serve traffic.',
  });

export type HealthResponse = z.infer<typeof healthResponseSchema>;

/**
 * The states a capability can be in.
 *
 * Deliberately small. States exist here only when something in the product
 * currently needs them; inventing DEGRADED or PREVIEW before anything reports
 * them would be taxonomy, and every client would have to handle a case that
 * cannot occur.
 */
export const CapabilityStates = ['LIVE', 'NOT_AVAILABLE'] as const;
export type CapabilityState = (typeof CapabilityStates)[number];

export const capabilityStateSchema = z.enum(CapabilityStates);

export const platformInfoResponseSchema = z
  .object({
    product_name: z.literal('Verydia Control'),
    vendor: z.literal('Parallax Motion'),
    api_version: z.string().describe('API surface version, e.g. "v1".'),
    platform_version: z.string().describe('Deployed platform build version.'),
    environment: z.enum(PlatformEnvironments),
    region: z.string().describe('Deployment region label, e.g. "us-east-1".'),
    documentation_url: z.url(),
    /**
     * What this deployment can actually do.
     *
     * A closed set of keys with a closed set of states, deliberately not
     * booleans. `false` cannot distinguish "not built yet" from "disabled here"
     * or "temporarily degraded", and a client reading a boolean has to invent
     * the difference — which is how a console ends up explaining an outage as a
     * roadmap.
     *
     * Capability is not authorization. A capability can be LIVE while a given
     * member lacks the role to use it; that question is answered by RBAC, on
     * every request, by the server.
     */
    capabilities: z
      .object({
        product_catalog: capabilityStateSchema,
        product_environments: capabilityStateSchema,
        licensed_product_plans: capabilityStateSchema,
        management_api: capabilityStateSchema,
        /** Machine-to-machine credentials. Distinct from `management_api`. */
        api_credentials: capabilityStateSchema,
        /*
          Split, because they stopped being one fact.

          `customer_license_core` bundled "can this vendor record who they sell
          to" with "can they issue a licence". While neither existed the bundle
          was harmless. The moment customers shipped it became a key that had to
          answer LIVE and NOT_AVAILABLE at the same time — so it would have had
          to lie in one direction or the other.
        */
        customers: capabilityStateSchema,
        license_core: capabilityStateSchema,
        runtime_validation: capabilityStateSchema,
        webhooks: capabilityStateSchema,
        offline_licensing: capabilityStateSchema,
      })
      .describe('What this deployment provides, by capability.'),
  })
  .meta({
    id: 'PlatformInfoResponse',
    title: 'PlatformInfoResponse',
    description: 'Platform metadata and live capability flags for this deployment.',
  });

export type PlatformInfoResponse = z.infer<typeof platformInfoResponseSchema>;
